import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Filter, X, Grid3X3, LayoutGrid, SlidersHorizontal, Sparkles } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useShopify } from '../context/ShopifyContext';
import { toast } from '../hooks/use-toast';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

// Loading skeleton
const ProductSkeleton = () => (
  <div className="animate-pulse">
    <div className="aspect-[3/4] rounded-2xl bg-gradient-to-br from-pink-100 to-rose-100 relative overflow-hidden">
      <motion.div 
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
        animate={{ x: ['-100%', '100%'] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
      />
    </div>
    <div className="mt-4 space-y-2">
      <div className="h-5 bg-pink-100 rounded w-3/4" />
      <div className="h-4 bg-pink-50 rounded w-1/2" />
    </div>
  </div>
);

// Sparkle background component
const SparkleBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(15)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute"
        initial={{ 
          x: `${Math.random() * 100}%`, 
          y: `${Math.random() * 100}%`,
          scale: 0,
          opacity: 0
        }}
        animate={{ 
          scale: [0, 1, 0],
          opacity: [0, 0.6, 0],
          rotate: [0, 180, 360]
        }}
        transition={{ 
          duration: 3,
          delay: i * 0.3,
          repeat: Infinity,
          repeatDelay: Math.random() * 2
        }}
      >
        <Sparkles size={16} className="text-pink-300" />
      </motion.div>
    ))}
  </div>
);

const CollectionPage = () => {
  const { slug } = useParams();
  const { 
    products, 
    productsLoading, 
    getCollectionByHandle,
    addToCart,
  } = useShopify();
  
  const [sortBy, setSortBy] = useState('featured');
  const [filterOpen, setFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [gridCols, setGridCols] = useState(4);

  // Reset filters when slug changes
  useEffect(() => {
    setSelectedSizes([]);
    setSelectedTypes([]);
    setPriceRange([0, 1000]);
    setAvailabilityFilter('all');
  }, [slug]);

  // Helper function to match word-start boundaries to avoid false positives
  const matchesWord = (text, word) => {
    const regex = new RegExp(`\\b${word}`, 'i');
    return regex.test(text);
  };

  // Check if product is jewelry (within Accessories productType)
  const isJewelry = (p) => {
    const title = (p.title || '').toLowerCase();
    const tags = (p.tags || []).map(t => t.toLowerCase());
    return matchesWord(title, 'necklace') || matchesWord(title, 'bracelet') || matchesWord(title, 'earring') ||
           matchesWord(title, 'hoops') || matchesWord(title, 'drops') || matchesWord(title, 'charm') ||
           matchesWord(title, 'stackable') || matchesWord(title, 'pendant') ||
           tags.some(t => matchesWord(t, 'jewelry') || matchesWord(t, 'necklace') || matchesWord(t, 'bracelet') || matchesWord(t, 'earring'));
  };

  // Check if product is a sweater/knit (within Tops productType)
  const isSweater = (p) => {
    const title = (p.title || '').toLowerCase();
    const tags = (p.tags || []).map(t => t.toLowerCase());
    const handle = (p.handle || '').toLowerCase();
    
    // Exclude "Cardigan & Shorts" - it belongs to Sets category only
    if (handle === 'cardigan-shorts' || title.includes('cardigan & shorts') || title.includes('cardigan shorts')) {
      return false;
    }
    
    return matchesWord(title, 'sweater') || matchesWord(title, 'knit') || matchesWord(title, 'cozy') ||
           matchesWord(title, 'snuggle') || matchesWord(title, 'cardigan') ||
           tags.some(t => matchesWord(t, 'sweater') || matchesWord(t, 'knit'));
  };

  // Helper function to filter products by category - using Shopify productType as primary filter
  const filterProductsByCategory = (allProducts, category) => {
    if (!category || category === 'all') return allProducts;
    
    return allProducts.filter(p => {
      const title = (p.title || '').toLowerCase();
      const productType = (p.productType || '').toLowerCase();
      const tags = (p.tags || []).map(t => t.toLowerCase());
      
      switch(category.toLowerCase()) {
        case 'sweaters':
          // Sweaters are in "Tops" productType but have sweater/knit keywords
          return isSweater(p) || tags.some(t => matchesWord(t, 'sweater'));
          
        case 'dresses':
          // Use Shopify's Dresses productType
          return productType === 'dresses' || tags.some(t => matchesWord(t, 'dress'));
          
        case 'tops':
          // Tops and Bodysuits, excluding sweaters
          const isTopsType = productType === 'tops' || productType === 'bodysuit' || productType === 'jackets';
          if (isTopsType && !isSweater(p)) return true;
          // Fallback for uncategorized tops
          if (!productType && (matchesWord(title, 'top') || matchesWord(title, 'sleeve') || matchesWord(title, 'tank'))) return true;
          return tags.some(t => matchesWord(t, 'top') || matchesWord(t, 'bodysuit'));
          
        case 'bodysuits':
          return productType === 'bodysuit' || tags.some(t => matchesWord(t, 'bodysuit'));
          
        case 'sunglasses':
          // Match sunglasses by title
          return matchesWord(title, 'sunglasses') || matchesWord(title, 'glasses') ||
                 tags.some(t => matchesWord(t, 'sunglasses') || matchesWord(t, 'eyewear'));
          
        case 'jewelry':
          // Jewelry items within Accessories productType
          if (productType === 'accessories' && isJewelry(p)) return true;
          // Also check tags for jewelry category
          return tags.some(t => matchesWord(t, 'jewelry'));
          
        case 'necklaces':
          return matchesWord(title, 'necklace') || matchesWord(title, 'pendant') ||
                 tags.some(t => matchesWord(t, 'necklace'));
                 
        case 'bracelets':
          return matchesWord(title, 'bracelet') || matchesWord(title, 'bangle') ||
                 tags.some(t => matchesWord(t, 'bracelet'));
                 
        case 'earrings':
          return matchesWord(title, 'earring') || matchesWord(title, 'hoops') || matchesWord(title, 'drops') ||
                 matchesWord(title, 'studs') || tags.some(t => matchesWord(t, 'earring'));
                 
        case 'sets':
          // Use Shopify's "2-piece sets" productType + pants + active sets
          if (productType === '2-piece sets' || productType === 'pants' || productType === 'active sets') return true;
          // Fallback for uncategorized sets
          if (!productType && (matchesWord(title, 'set') || matchesWord(title, 'lounge') || 
              matchesWord(title, 'jogger') || matchesWord(title, 'sweatpants'))) return true;
          return tags.some(t => matchesWord(t, 'set') || matchesWord(t, '2-piece'));
          
        case 'shorts':
          // Judy Blue Jeans and shorts
          if (productType === 'judy blue jeans') return true;
          return matchesWord(title, 'shorts') || matchesWord(title, 'jeans') ||
                 tags.some(t => matchesWord(t, 'shorts') || matchesWord(t, 'jeans'));
          
        case 'accessories':
          // Non-jewelry accessories (keychains, bags, sunglasses, etc.)
          if (productType === 'accessories' && !isJewelry(p)) return true;
          // Fallback for uncategorized accessories
          if (!productType && (matchesWord(title, 'keychain') || matchesWord(title, 'bag'))) return true;
          return tags.some(t => matchesWord(t, 'accessory') && !matchesWord(t, 'jewelry'));
          
        default:
          return true;
      }
    });
  };

  // Category titles mapping
  const categoryTitles = {
    'all': 'All Products',
    'sweaters': 'Sweaters & Knits',
    'dresses': 'Dresses',
    'tops': 'Tops & Bodysuits',
    'bodysuits': 'Bodysuits',
    'sunglasses': 'Sunglasses',
    'jewelry': 'Jewelry',
    'necklaces': 'Necklaces',
    'bracelets': 'Bracelets',
    'earrings': 'Earrings',
    'sets': 'Sets & Loungewear',
    'shorts': 'Shorts & Jeans',
    'accessories': 'Accessories',
    'best-sellers': 'Best Sellers',
    'new-arrivals': 'New Arrivals'
  };

  // Compute collection directly from products and slug - no async needed
  const collection = useMemo(() => {
    if (products.length === 0) return null;
    
    const categoryKey = slug?.toLowerCase() || 'all';
    let filteredProducts = filterProductsByCategory(products, categoryKey);
    
    // If no products found for the category, show all products
    if (filteredProducts.length === 0 && categoryKey !== 'all') {
      filteredProducts = products;
    }
    
    // Pin "Cardigan & Shorts" to the front for Sets
    if (categoryKey === 'sets') {
      filteredProducts = [
        ...filteredProducts.filter(p => p.title === 'Cardigan & Shorts'),
        ...filteredProducts.filter(p => p.title !== 'Cardigan & Shorts'),
      ];
    }
    
    const title = categoryTitles[categoryKey] || 
                 (slug ? slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ') : 'All Products');
    
    return {
      title: title,
      handle: slug || 'all',
      description: `Shop our ${title.toLowerCase()} collection`,
      products: filteredProducts
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, products]);

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const collectionProducts = collection?.products || [];
    const sizes = new Set();
    const types = new Set();
    let maxPrice = 0;

    collectionProducts.forEach(p => {
      p.sizes?.forEach(s => sizes.add(s));
      if (p.productType) types.add(p.productType);
      if (p.price > maxPrice) maxPrice = p.price;
    });

    return {
      sizes: Array.from(sizes).sort(),
      types: Array.from(types).sort(),
      maxPrice: Math.ceil(maxPrice / 10) * 10 || 500
    };
  }, [collection]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = collection?.products || [];

    // Apply filters
    if (selectedSizes.length > 0) {
      result = result.filter(p => 
        p.sizes?.some(s => selectedSizes.includes(s))
      );
    }

    if (selectedTypes.length > 0) {
      result = result.filter(p => selectedTypes.includes(p.productType));
    }

    if (availabilityFilter === 'in-stock') {
      result = result.filter(p => p.availableForSale);
    } else if (availabilityFilter === 'out-of-stock') {
      result = result.filter(p => !p.availableForSale);
    }

    result = result.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Sort
    switch (sortBy) {
      case 'price-low':
        result = [...result].sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result = [...result].sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        result = [...result].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
      case 'best-selling':
        result = [...result].sort((a, b) => (b.isBestSeller ? 1 : 0) - (a.isBestSeller ? 1 : 0));
        break;
      default:
        break;
    }

    return result;
  }, [collection, sortBy, selectedSizes, selectedTypes, priceRange, availabilityFilter]);

  const handleAddToCart = async (product, variant) => {
    try {
      await addToCart(product, variant);
      toast({
        title: "Added to cart!",
        description: `${product.title} has been added to your cart.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add item to cart.",
        variant: "destructive"
      });
    }
  };

  const clearFilters = () => {
    setSelectedSizes([]);
    setSelectedTypes([]);
    setPriceRange([0, filterOptions.maxPrice]);
    setAvailabilityFilter('all');
  };

  const activeFiltersCount = selectedSizes.length + selectedTypes.length + 
    (availabilityFilter !== 'all' ? 1 : 0) +
    (priceRange[0] > 0 || priceRange[1] < filterOptions.maxPrice ? 1 : 0);

  if (!collection && productsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white pt-8 relative">
        <SparkleBackground />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse mb-12">
            <div className="h-12 bg-pink-100 rounded w-1/3 mb-4" />
            <div className="h-6 bg-pink-50 rounded w-1/2" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => <ProductSkeleton key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white relative">
      {/* Hero Banner */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative h-48 md:h-64 bg-gradient-to-r from-pink-100 via-rose-50 to-pink-100 overflow-hidden"
      >
        <SparkleBackground />
        {collection?.image?.url && (
          <motion.img 
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1 }}
            src={collection.image.url} 
            alt={collection.title}
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-pink-50/90" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-8">
          {/* Breadcrumb */}
          <motion.nav 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm mb-2"
          >
            <Link to="/" className="text-pink-600 hover:text-pink-700">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-600">{collection?.title}</span>
          </motion.nav>
          
          <motion.div className="flex items-center gap-3">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold text-gray-900"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {collection?.title}
            </motion.h1>
            <motion.div
              animate={{ 
                rotate: [0, 15, -15, 0],
                scale: [1, 1.2, 1]
              }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
            >
              <Sparkles className="text-pink-500" size={28} />
            </motion.div>
          </motion.div>
          
          {collection?.description && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-gray-600 mt-2 max-w-2xl italic"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {collection.description}
            </motion.p>
          )}
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Toolbar */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-6 border-b border-pink-100"
        >
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setFilterOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-pink-200 rounded-xl hover:border-pink-400 hover:shadow-md transition-all"
            >
              <SlidersHorizontal size={18} className="text-pink-500" />
              <span className="font-medium text-gray-700">Filters</span>
              {activeFiltersCount > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-pink-500 text-white text-xs rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </motion.button>
            
            <motion.span 
              className="text-gray-500 flex items-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Sparkles size={14} className="text-pink-400" />
              {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            </motion.span>
          </div>

          <div className="flex items-center gap-4">
            {/* Grid toggle */}
            <div className="hidden md:flex items-center gap-1 bg-white border border-pink-200 rounded-xl p-1">
              {[2, 3, 4].map((cols) => (
                <button
                  key={cols}
                  onClick={() => setGridCols(cols)}
                  className={`p-2 rounded-lg transition-colors ${
                    gridCols === cols ? 'bg-pink-100 text-pink-600' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {cols === 2 ? <LayoutGrid size={18} /> : <Grid3X3 size={18} />}
                </button>
              ))}
            </div>

            {/* Sort dropdown */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 bg-white border border-pink-200 rounded-xl text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-pink-300 cursor-pointer"
              >
                <option value="featured">Featured</option>
                <option value="newest">Newest</option>
                <option value="best-selling">Best Selling</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-pink-400 pointer-events-none" size={18} />
            </div>
          </div>
        </motion.div>

        {/* Active Filters Tags */}
        <AnimatePresence>
          {activeFiltersCount > 0 && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex flex-wrap gap-2 mb-6"
            >
              {selectedSizes.map(size => (
                <motion.span
                  key={size}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="px-3 py-1.5 bg-pink-100 text-pink-700 rounded-full text-sm flex items-center gap-1"
                >
                  Size: {size}
                  <button onClick={() => setSelectedSizes(prev => prev.filter(s => s !== size))}>
                    <X size={14} />
                  </button>
                </motion.span>
              ))}
              {selectedTypes.map(type => (
                <motion.span
                  key={type}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="px-3 py-1.5 bg-pink-100 text-pink-700 rounded-full text-sm flex items-center gap-1"
                >
                  {type}
                  <button onClick={() => setSelectedTypes(prev => prev.filter(t => t !== type))}>
                    <X size={14} />
                  </button>
                </motion.span>
              ))}
              <button 
                onClick={clearFilters}
                className="px-3 py-1.5 text-pink-600 hover:text-pink-700 text-sm font-medium underline"
              >
                Clear all
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className={`grid gap-6 ${
              gridCols === 2 ? 'grid-cols-1 sm:grid-cols-2' :
              gridCols === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' :
              'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
            }`}
          >
            {filteredProducts.map((product, index) => (
              <motion.div key={product.id} variants={fadeInUp}>
                <ProductCard 
                  product={product} 
                  onAddToCart={handleAddToCart}
                  index={index}
                />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20 relative"
          >
            <SparkleBackground />
            <motion.div 
              className="text-6xl mb-4"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              🌸
            </motion.div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No products found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your filters or browse our collections</p>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={clearFilters}
              className="px-6 py-3 bg-pink-500 text-white rounded-full font-medium hover:bg-pink-600 transition-colors"
            >
              Clear Filters
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Filter Sidebar */}
      <AnimatePresence>
        {filterOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setFilterOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed left-0 top-0 h-full w-80 bg-white z-50 shadow-2xl overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    Filters
                    <Sparkles size={18} className="text-pink-500" />
                  </h2>
                  <button 
                    onClick={() => setFilterOpen(false)}
                    className="p-2 hover:bg-pink-50 rounded-full transition-colors"
                  >
                    <X size={24} className="text-gray-500" />
                  </button>
                </div>

                {/* Availability */}
                <div className="mb-6 pb-6 border-b border-pink-100">
                  <h3 className="font-semibold text-gray-900 mb-3">Availability</h3>
                  <div className="space-y-2">
                    {['all', 'in-stock', 'out-of-stock'].map((opt) => (
                      <label key={opt} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="availability"
                          checked={availabilityFilter === opt}
                          onChange={() => setAvailabilityFilter(opt)}
                          className="w-4 h-4 text-pink-500 focus:ring-pink-400"
                        />
                        <span className="text-gray-700 capitalize">
                          {opt === 'all' ? 'All' : opt === 'in-stock' ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div className="mb-6 pb-6 border-b border-pink-100">
                  <h3 className="font-semibold text-gray-900 mb-3">Price Range</h3>
                  <div className="flex items-center gap-4">
                    <input
                      type="number"
                      value={priceRange[0]}
                      onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])}
                      className="w-20 px-3 py-2 border border-pink-200 rounded-lg text-center"
                      min="0"
                    />
                    <span className="text-gray-400">to</span>
                    <input
                      type="number"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                      className="w-20 px-3 py-2 border border-pink-200 rounded-lg text-center"
                      max={filterOptions.maxPrice}
                    />
                  </div>
                </div>

                {/* Sizes */}
                {filterOptions.sizes.length > 0 && (
                  <div className="mb-6 pb-6 border-b border-pink-100">
                    <h3 className="font-semibold text-gray-900 mb-3">Sizes</h3>
                    <div className="flex flex-wrap gap-2">
                      {filterOptions.sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => {
                            setSelectedSizes(prev => 
                              prev.includes(size) 
                                ? prev.filter(s => s !== size)
                                : [...prev, size]
                            );
                          }}
                          className={`px-4 py-2 rounded-lg border transition-all ${
                            selectedSizes.includes(size)
                              ? 'bg-pink-500 text-white border-pink-500'
                              : 'bg-white text-gray-700 border-pink-200 hover:border-pink-400'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={clearFilters}
                    className="flex-1 py-3 border border-pink-200 text-pink-600 rounded-xl font-medium hover:bg-pink-50 transition-colors"
                  >
                    Clear All
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setFilterOpen(false)}
                    className="flex-1 py-3 bg-pink-500 text-white rounded-xl font-medium hover:bg-pink-600 transition-colors"
                  >
                    Apply
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CollectionPage;
